import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, Box, Typography, styled } from '@mui/material';
import type { AccordionSummaryOwnProps } from '@mui/material/AccordionSummary';
import MuiAccordionSummary, { accordionSummaryClasses } from '@mui/material/AccordionSummary';
import React from 'react';
import { useSelector } from 'react-redux';
import {
	useSearchAssetNumbersQuery as useAssetSearch,
	useSearchSerialNumbersQuery as useSerialSearch,
} from '../../../api/filters.api';
import type { Asset } from '../../../api/models/asset.model';
import type { FilterField } from '../../../api/models/filters.model';
import { ASSET_NUMBER, SERIAL_NO } from '../../../constants/messages';
import { selectAccount } from '../../../redux/slices/accountSlice';
import { PropToString } from '../../../utils/helpers';
import AccountNameFilterField from '../Fields/AccountNameFilterField';
import ChipFilterField from '../Fields/ChipFilterField';
import DateRangeFilterField from '../Fields/DateRangeFilterField';
import NumberRangeFilterField from '../Fields/NumberRangeFilterField';
import SearchableFilterField from '../Fields/SearchableFilterField';
import StringFilterField from '../Fields/StringFilterField';

interface FilterFieldAccordionProps {
	field: FilterField;
	pendingFilterValues: { [key: string]: any };
	handleFieldChange: (field: FilterField, value: any) => void;
}

export const assetProps = PropToString<Asset>();

const AccordionSummary = styled((props: AccordionSummaryOwnProps) => (
	<MuiAccordionSummary expandIcon={<ExpandMoreIcon />} {...props} />
))(({ theme }) => ({
	flexDirection: 'row',
	[`&.${accordionSummaryClasses.root}`]: {
		justifyContent: 'left',
		minHeight: 30,
		height: 30,
		padding: theme.spacing(0, 0, 0, 0),
	},
	[`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
		transform: 'rotate(180deg)',
	},
	[`& .${accordionSummaryClasses.content}`]: {
		padding: theme.spacing(0, 1, 0, 0),
		width: 'fit-content',
		flexGrow: 0,
	},
}));

function FilterFieldAccordion(props: FilterFieldAccordionProps) {
	const { field, pendingFilterValues, handleFieldChange } = props;
	const account = useSelector(selectAccount);

	// State for searchable fields

	// Controlled input state for asset/serial number search fields
	const [assetNumberSearch, setAssetNumberSearch] = React.useState('');
	const [assetNumberInput, setAssetNumberInput] = React.useState('');
	const [serialNumberSearch, setSerialNumberSearch] = React.useState('');
	const [serialNumberInput, setSerialNumberInput] = React.useState('');

	// API hooks for searchable fields (conditionally skip if not the right field)

	// Debounce search for asset number
	React.useEffect(() => {
		const timer = setTimeout(() => {
			if (assetNumberInput.length >= 3) {
				setAssetNumberSearch(assetNumberInput);
			}
		}, 1000);
		return () => clearTimeout(timer);
	}, [assetNumberInput]);

	// Debounce search for serial number
	React.useEffect(() => {
		const timer = setTimeout(() => {
			if (serialNumberInput.length >= 3) {
				setSerialNumberSearch(serialNumberInput);
			}
		}, 1000);
		return () => clearTimeout(timer);
	}, [serialNumberInput]);

	const { data: assetNumberResults = [], isFetching: isAssetNumberLoading } = useAssetSearch(
		{
			accountId: account?.accountId || 0,
			accountTypeCode: account?.accountTypeCode || '',
			sourceId: account?.sourceId || 0,
			filters: pendingFilterValues,
			searchString: assetNumberSearch,
		},
		{
			skip:
				field.name !== assetProps.assetNumber || assetNumberInput.length < 3 || !account?.accountId,
		}
	);

	const { data: serialNumberResults = [], isFetching: isSerialNumberLoading } = useSerialSearch(
		{
			accountId: account?.accountId || 0,
			accountTypeCode: account?.accountTypeCode || '',
			sourceId: account?.sourceId || 0,
			filters: pendingFilterValues,
			searchString: serialNumberSearch,
		},
		{
			skip:
				field.name !== assetProps.serialNumber ||
				serialNumberInput.length < 3 ||
				!account?.accountId,
		}
	);

	// Memoize handlers for child components
	const handleChangeMemo = React.useCallback(
		(val: any) => {
			handleFieldChange(field, val);
			// If this is a searchable field, clear the input after selection to prevent loading indicator
			if (field.searchable && field.name === assetProps.assetNumber) {
				setAssetNumberInput('');
			} else if (field.searchable && field.name === assetProps.serialNumber) {
				setSerialNumberInput('');
			}
		},
		[handleFieldChange, field]
	);
	const handleMinChangeMemo = React.useCallback(
		(val: any) => handleFieldChange({ ...field, name: `${field.name}_min` }, val),
		[handleFieldChange, field]
	);
	const handleMaxChangeMemo = React.useCallback(
		(val: any) => handleFieldChange({ ...field, name: `${field.name}_max` }, val),
		[handleFieldChange, field]
	);

	function renderContent() {
		// Handle searchable fields (asset number and serial number)
		if (
			field.searchable &&
			(field.name === assetProps.assetNumber || field.name === assetProps.serialNumber)
		) {
			const isAssetNumber = field.name === assetProps.assetNumber;
			const label = isAssetNumber ? ASSET_NUMBER : SERIAL_NO;
			return (
				<SearchableFilterField
					field={{ ...field, label }}
					value={
						Array.isArray(pendingFilterValues[field.name]) ? pendingFilterValues[field.name] : []
					}
					onChange={handleChangeMemo}
					searchResults={isAssetNumber ? assetNumberResults : serialNumberResults}
					loading={isAssetNumber ? isAssetNumberLoading : isSerialNumberLoading}
					inputValue={isAssetNumber ? assetNumberInput : serialNumberInput}
					onInputChange={isAssetNumber ? setAssetNumberInput : setSerialNumberInput}
				/>
			);
		}

		switch (field.type) {
			case 'object':
				return (
					<AccountNameFilterField
						values={Array.isArray(field.values) ? (field.values as any) : []}
						label={field.label}
						value={
							Array.isArray(pendingFilterValues[field.name]) ? pendingFilterValues[field.name] : []
						}
						onChange={handleChangeMemo}
					/>
				);
			case 'string':
				return (
					<StringFilterField
						field={field}
						value={
							Array.isArray(pendingFilterValues[field.name]) ? pendingFilterValues[field.name] : []
						}
						onChange={handleChangeMemo}
					/>
				);
			case 'chip':
				return (
					<ChipFilterField
						field={field}
						value={
							Array.isArray(pendingFilterValues[field.name]) ? pendingFilterValues[field.name] : []
						}
						onChange={handleChangeMemo}
					/>
				);
			case 'number':
				if (field.min !== undefined && field.max !== undefined && field.min < field.max) {
					return (
						<NumberRangeFilterField
							field={field}
							minValue={pendingFilterValues[`${field.name}_min`] || ''}
							maxValue={pendingFilterValues[`${field.name}_max`] || ''}
							onMinChange={handleMinChangeMemo}
							onMaxChange={handleMaxChangeMemo}
						/>
					);
				} else {
					return (
						<NumberRangeFilterField
							field={field}
							minValue={pendingFilterValues[field.name] || ''}
							maxValue={''}
							onMinChange={handleChangeMemo}
							onMaxChange={() => {}}
						/>
					);
				}
			case 'date':
				return (
					<DateRangeFilterField
						field={field}
						minValue={pendingFilterValues[`${field.name}_min`] || null}
						maxValue={pendingFilterValues[`${field.name}_max`] || null}
						onMinChange={handleMinChangeMemo}
						onMaxChange={handleMaxChangeMemo}
					/>
				);
			default:
				return null;
		}
	}

	return (
		<Accordion
			elevation={0}
			key={field.name}
			defaultExpanded={true}
			data-testid={`filter-accordion-field-${field.name}`}
			sx={{
				'&::before': {
					display: 'none', // removes the horizontal border
				},
			}}
		>
			<AccordionSummary>
				<Typography>{field.label}</Typography>
			</AccordionSummary>
			<AccordionDetails>
				<Box>{renderContent()}</Box>
			</AccordionDetails>
		</Accordion>
	);
}

export default FilterFieldAccordion;
